import React, { useState } from "react";
import { formatMoney, getStatusBadgeClass, getStatusLabel } from "./utils";

const COLOR_TOKENS = ["color", "colors", "colour", "colours", "لون", "الوان", "ألوان"];
const SIZE_TOKENS = ["size", "sizes", "مقاس", "مقاسات", "حجم", "احجام", "أحجام"];

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase();
}

function attributeMatches(attribute, tokens) {
  const candidates = [attribute?.slug, attribute?.nameAr, attribute?.nameEn].map(normalizeToken);
  return candidates.some((candidate) => candidate && tokens.includes(candidate));
}

function variantOptionSummary(variant, isAr) {
  const labels = Array.isArray(variant.options)
    ? variant.options.map((option) => (isAr ? option.label_ar || option.label_en : option.label_en || option.label_ar))
    : [];

  return labels.filter(Boolean).join(" / ");
}

function sellerVariantImage(variant) {
  return variant.image || "";
}

function validateImageFile(file) {
  if (!file) return { valid: true };
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: "Only JPG, PNG, WebP, and GIF images are allowed" };
  }
  if (file.size > maxSize) {
    return { valid: false, error: "Image must be smaller than 5MB" };
  }
  return { valid: true };
}

function validateDigitalFile(file) {
  if (!file) return { valid: true };
  const maxSize = 50 * 1024 * 1024; // 50MB
  const allowedTypes = ['application/pdf', 'application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: "Only PDF, ZIP, RAR, 7Z, DOC, DOCX, XLS, XLSX, and TXT files are allowed" };
  }
  if (file.size > maxSize) {
    return { valid: false, error: "File must be smaller than 50MB" };
  }
  return { valid: true };
}

export default function SellerProductsPanel({
  sellerProducts,
  sellerMeta,
  sellerMarketplaceEnabled,
  sellerProfile,
  sellerForm,
  sellerVariantForm,
  activeSellerVariantProduct,
  sellerOptionAttributes,
  updateSellerField,
  updateSellerVariantField,
  setSellerForm,
  setSellerVariantForm,
  resetSellerForm,
  resetSellerVariantForm,
  savingSellerProduct,
  savingSellerVariant,
  handleSaveSellerProduct,
  handleSaveSellerVariant,
  handleDeleteSellerProduct,
  handleDeleteSellerVariant,
  handleEditSellerProduct,
  handleEditSellerVariant,
  handleManageSellerVariants,
  currency,
  isAdmin,
  isAr,
  isMobile,
}) {
  const [variantImageError, setVariantImageError] = useState("");
  const [productImageError, setProductImageError] = useState("");
  const [digitalFileError, setDigitalFileError] = useState("");

  const selectedAttributeId = Number(sellerVariantForm.attributeId || 0);
  const selectedVariantValues = Array.isArray(sellerMeta.attributeValues)
    ? sellerMeta.attributeValues.filter((item) => item.attributeId === selectedAttributeId)
    : [];
  const colorAttribute = sellerOptionAttributes.find((attribute) => attributeMatches(attribute, COLOR_TOKENS));
  const sizeAttribute = sellerOptionAttributes.find((attribute) => attributeMatches(attribute, SIZE_TOKENS));
  const colorValues = Array.isArray(sellerMeta.attributeValues)
    ? sellerMeta.attributeValues.filter((item) => item.attributeId === colorAttribute?.id)
    : [];
  const sizeValues = Array.isArray(sellerMeta.attributeValues)
    ? sellerMeta.attributeValues.filter((item) => item.attributeId === sizeAttribute?.id)
    : [];

  const renderVariantManager = () => {
    if (!activeSellerVariantProduct) {
      return (
        <div className="alert alert-secondary mt-4 mb-0">
          {isAr
            ? "اختر منتجًا من القائمة بالأسفل لإدارة الصور المتعددة، المتغيرات، SKU، والمخزون."
            : "Choose a product below to manage multiple images, variants, SKU, and stock rules."}
        </div>
      );
    }

    if (activeSellerVariantProduct.isDigital) {
      return (
        <div className="alert alert-secondary mt-4 mb-0">
          {isAr
            ? "المنتجات الرقمية تستخدم متغيرًا افتراضيًا واحدًا فقط، لذلك إدارة المتغيرات اليدوية غير متاحة هنا."
            : "Digital products use one auto-managed default variant, so manual variant management is disabled here."}
        </div>
      );
    }

    return (
      <div className="mt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h6 className="mb-1">
              {isAr ? "صور المنتج والمتغيرات" : "Product images & variants"}
            </h6>
            <div className="text-muted small">
              {isAr
                ? "الصورة الرئيسية تبقى على المنتج نفسه، وكل متغير يمكنه حمل صورة خاصة به. بهذه الطريقة تحصل على صور متعددة فعلية."
                : "The main image stays on the product, and each variant can carry its own image for true multi-image product coverage."}
            </div>
          </div>
          <span className="badge text-bg-light">
            {activeSellerVariantProduct.variants?.length || 0}
          </span>
        </div>

        <div className="alert alert-info">
          {isAr
            ? "أي تعديل على المتغيرات يعيد المنتج إلى المراجعة. استخدم Option Type مع خياره، وأضف لونًا أو مقاسًا إضافيًا عند الحاجة."
            : "Any variant change sends the product back to review. Use an option type with its option, then add a color or size when needed."}
        </div>

        <form onSubmit={handleSaveSellerVariant}>
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">{isAr ? "نوع الخيار" : "Option Type"}</label>
              <select
                className="form-select"
                value={sellerVariantForm.attributeId}
                onChange={(event) => {
                  const nextAttributeId = event.target.value;
                  setSellerVariantForm((current) => ({
                    ...current,
                    attributeId: nextAttributeId,
                    valueId: "",
                  }));
                }}
              >
                <option value="">{isAr ? "اختر النوع" : "Select type"}</option>
                {sellerOptionAttributes.map((attribute) => (
                  <option key={attribute.id} value={attribute.id}>
                    {isAr ? attribute.nameAr || attribute.nameEn : attribute.nameEn || attribute.nameAr}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">{isAr ? "الخيار الأساسي" : "Primary Option"}</label>
              <select
                className="form-select"
                value={sellerVariantForm.valueId}
                disabled={!selectedAttributeId}
                onChange={(event) => updateSellerVariantField("valueId", event.target.value)}
              >
                <option value="">{isAr ? "اختر الخيار" : "Select option"}</option>
                {selectedVariantValues.map((value) => (
                  <option key={value.id} value={value.id}>
                    {isAr ? value.valueAr || value.valueEn : value.valueEn || value.valueAr}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">{isAr ? "SKU" : "SKU"}</label>
              <input
                className="form-control"
                value={sellerVariantForm.sku}
                onChange={(event) => updateSellerVariantField("sku", event.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "اللون" : "Color"}</label>
              <select
                className="form-select"
                value={sellerVariantForm.colorId}
                onChange={(event) => updateSellerVariantField("colorId", event.target.value)}
              >
                <option value="">{isAr ? "بدون" : "Optional"}</option>
                {colorValues.map((value) => (
                  <option key={value.id} value={value.id}>
                    {isAr ? value.valueAr || value.valueEn : value.valueEn || value.valueAr}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "المقاس" : "Size"}</label>
              <select
                className="form-select"
                value={sellerVariantForm.sizeId}
                onChange={(event) => updateSellerVariantField("sizeId", event.target.value)}
              >
                <option value="">{isAr ? "بدون" : "Optional"}</option>
                {sizeValues.map((value) => (
                  <option key={value.id} value={value.id}>
                    {isAr ? value.valueAr || value.valueEn : value.valueEn || value.valueAr}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "السعر" : "Price"}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={sellerVariantForm.price}
                onChange={(event) => updateSellerVariantField("price", event.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "سعر العرض" : "Sale Price"}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={sellerVariantForm.salePrice}
                onChange={(event) => updateSellerVariantField("salePrice", event.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "المخزون" : "Stock"}</label>
              <input
                type="number"
                min="0"
                className="form-control"
                value={sellerVariantForm.stock}
                onChange={(event) => updateSellerVariantField("stock", event.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">{isAr ? "تنبيه المخزون المنخفض" : "Low Stock Alert"}</label>
              <input
                type="number"
                min="0"
                className="form-control"
                value={sellerVariantForm.lowStockAlert}
                onChange={(event) => updateSellerVariantField("lowStockAlert", event.target.value)}
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label">{isAr ? "صورة المتغير" : "Variant Image"}</label>
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  if (!file) {
                    setSellerVariantForm((current) => ({
                      ...current,
                      imageFile: null,
                      imagePreview: current.imagePreview,
                    }));
                    return;
                  }
                  const validation = validateImageFile(file);
                  if (!validation.valid) {
                    setVariantImageError(validation.error);
                    event.target.value = "";
                    return;
                  }
                  setVariantImageError("");
                  setSellerVariantForm((current) => ({
                    ...current,
                    imageFile: file,
                    imagePreview: URL.createObjectURL(file),
                  }));
                }}
              />
              {variantImageError && <div className="alert alert-danger py-2 mt-2 mb-2">{variantImageError}</div>}
              {(sellerVariantForm.imagePreview || sellerVariantForm.image) ? (
                <div className="mt-2">
                  <img
                    src={sellerVariantForm.imagePreview || sellerVariantForm.image}
                    alt="Variant preview"
                    style={{
                      width: 88,
                      height: 88,
                      objectFit: "cover",
                      borderRadius: 14,
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                    }}
                  />
                </div>
              ) : null}
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label">{isAr ? "اسم ملف أو رابط احتياطي" : "Fallback Image URL or Filename"}</label>
              <input
                className="form-control"
                value={sellerVariantForm.image}
                onChange={(event) => updateSellerVariantField("image", event.target.value)}
              />
            </div>
          </div>

          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-dark" type="submit" disabled={savingSellerVariant}>
              {savingSellerVariant
                ? isAr
                  ? "جارٍ الحفظ..."
                  : "Saving..."
                : sellerVariantForm.id
                  ? isAr
                    ? "تحديث المتغير"
                    : "Update Variant"
                  : isAr
                    ? "إضافة متغير"
                    : "Add Variant"}
            </button>
            {sellerVariantForm.id ? (
              <button className="btn btn-outline-secondary" type="button" onClick={resetSellerVariantForm}>
                {isAr ? "إلغاء" : "Cancel"}
              </button>
            ) : null}
          </div>
        </form>

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{isAr ? "المتغيرات الحالية" : "Current Variants"}</h6>
            <span className="badge text-bg-light">
              {activeSellerVariantProduct.variants?.length || 0}
            </span>
          </div>

          {activeSellerVariantProduct.variants?.length ? (
            <div className="row g-3">
              {activeSellerVariantProduct.variants.map((variant) => (
                <div className="col-12 col-lg-6" key={variant.id}>
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <div className="fw-semibold">
                            {variantOptionSummary(variant, isAr) || `${isAr ? "متغير" : "Variant"} #${variant.id}`}
                          </div>
                          <div className="small text-muted">
                            SKU: {variant.sku || "-"}
                          </div>
                        </div>
                        {sellerVariantImage(variant) ? (
                          <img
                            src={sellerVariantImage(variant)}
                            alt=""
                            style={{
                              width: 72,
                              height: 72,
                              objectFit: "cover",
                              borderRadius: 14,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                            }}
                          />
                        ) : null}
                      </div>

                      <div className="row g-2 mt-2 small">
                        <div className="col-6">
                          <strong>{isAr ? "السعر:" : "Price:"}</strong>{" "}
                          {formatMoney(variant.price, currency || "USD")}
                        </div>
                        <div className="col-6">
                          <strong>{isAr ? "العرض:" : "Sale:"}</strong>{" "}
                          {variant.salePrice > 0
                            ? formatMoney(variant.salePrice, currency || "USD")
                            : "-"}
                        </div>
                        <div className="col-6">
                          <strong>{isAr ? "المخزون:" : "Stock:"}</strong> {variant.stock}
                        </div>
                        <div className="col-6">
                          <strong>{isAr ? "الحد الأدنى:" : "Low alert:"}</strong>{" "}
                          {variant.lowStockAlert}
                        </div>
                      </div>

                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleEditSellerVariant(activeSellerVariantProduct, variant)}
                        >
                          {isAr ? "تعديل" : "Edit"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteSellerVariant(activeSellerVariantProduct, variant)}
                        >
                          {isAr ? "حذف" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">
              {isAr
                ? "لا توجد متغيرات فعلية بعد. أضف أول متغير وسيحل محل المتغير الافتراضي القديم تلقائيًا."
                : "No real variants yet. Add the first one and it will automatically replace the old default system variant."}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card border-0 shadow-sm mb-4 provider-center__panel-card pc-panel--seller" id="seller-products-panel">
      <div className="card-body">
        <div className="pc-section-head mb-3">
          <div className="pc-section-head__left">
            <span className="pc-icon-box pc-icon-box--amber"><i className="bi bi-bag-check" /></span>
            <h5 className="mb-0">{isAr ? "منتجات البائع" : "Seller Products"}</h5>
          </div>
          <span className="badge text-bg-light">{sellerProducts.length}</span>
        </div>

        {!sellerMarketplaceEnabled ? (
          <div className="alert alert-warning mb-0">
            {isAr
              ? "قسم البائعين معطل حاليًا من لوحة الإدارة. المنتجات الحالية لن تظهر للزوار حتى يتم تفعيل القسم مرة أخرى."
              : "The seller section is currently disabled from admin settings. Existing seller products will stay hidden from customers until it is enabled again."}
          </div>
        ) : !isAdmin && !sellerProfile ? (
          <div className="text-muted">
            {isAr
              ? "أرسل طلب اعتماد كبائع أولًا، وبعد الموافقة ستتمكن من إضافة المنتجات هنا."
              : "Apply as a seller first. Once approved, you will be able to submit products here."}
          </div>
        ) : !isAdmin && sellerProfile.status !== "approved" ? (
          <div className="text-muted">
            {isAr
              ? "ملف البائع ما زال بانتظار موافقة الإدارة. بعد اعتماده ستتمكن من إرسال المنتجات للمراجعة."
              : "Your seller profile is still waiting for admin approval. Once approved, you can submit products for review."}
          </div>
        ) : (
          <>
            <div className="alert alert-info">
              {isAr
                ? "أضف صورة رئيسية للمنتج، ثم أضف صورًا إضافية عبر صور المتغيرات. كل تعديل على المنتج أو متغيراته يعيد العنصر للمراجعة."
                : "Add a main product image, then extend coverage with variant images. Any product or variant change sends the item back to review."}
            </div>

            <form onSubmit={handleSaveSellerProduct}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">{isAr ? "اسم المنتج بالعربية" : "Name Arabic"}</label>
                  <input className="form-control" value={sellerForm.nameAr} onChange={(event) => updateSellerField("nameAr", event.target.value)} />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{isAr ? "اسم المنتج بالإنجليزية" : "Name English"}</label>
                  <input className="form-control" value={sellerForm.nameEn} onChange={(event) => updateSellerField("nameEn", event.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">{isAr ? "الوصف بالعربية" : "Description Arabic"}</label>
                  <textarea rows="3" className="form-control" value={sellerForm.descriptionAr} onChange={(event) => updateSellerField("descriptionAr", event.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">{isAr ? "الوصف بالإنجليزية" : "Description English"}</label>
                  <textarea rows="3" className="form-control" value={sellerForm.descriptionEn} onChange={(event) => updateSellerField("descriptionEn", event.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">{isAr ? "القسم" : "Category"}</label>
                  <select className="form-select" value={sellerForm.categoryId} onChange={(event) => updateSellerField("categoryId", event.target.value)}>
                    <option value="">{isAr ? "اختر القسم" : "Select category"}</option>
                    {sellerMeta.categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {isAr ? category.nameAr || category.nameEn : category.nameEn || category.nameAr}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">{isAr ? "السعر" : "Price"}</label>
                  <input type="number" min="0" step="0.01" className="form-control" value={sellerForm.price} onChange={(event) => updateSellerField("price", event.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">{isAr ? "سعر العرض" : "Sale Price"}</label>
                  <input type="number" min="0" step="0.01" className="form-control" value={sellerForm.salePrice} onChange={(event) => updateSellerField("salePrice", event.target.value)} />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{isAr ? "صورة المنتج الرئيسية" : "Main Product Image"}</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      if (!file) {
                        setSellerForm((current) => ({
                          ...current,
                          imageFile: null,
                          imagePreview: current.imagePreview,
                        }));
                        return;
                      }
                      const validation = validateImageFile(file);
                      if (!validation.valid) {
                        setProductImageError(validation.error);
                        event.target.value = "";
                        return;
                      }
                      setProductImageError("");
                      setSellerForm((current) => ({
                        ...current,
                        imageFile: file,
                        imagePreview: URL.createObjectURL(file),
                      }));
                    }}
                  />
                  {productImageError && <div className="alert alert-danger py-2 mt-2 mb-2">{productImageError}</div>}
                  {sellerForm.imagePreview || sellerForm.image ? (
                    <div className="mt-2">
                      <img
                        src={sellerForm.imagePreview || sellerForm.image}
                        alt={sellerForm.nameEn || sellerForm.nameAr || "Seller product preview"}
                        style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 16, border: "1px solid rgba(15, 23, 42, 0.08)" }}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{isAr ? "رابط صورة أو اسم ملف احتياطي" : "Fallback Image URL or Filename"}</label>
                  <input className="form-control" value={sellerForm.image} onChange={(event) => updateSellerField("image", event.target.value)} />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">{isAr ? "اللون العام" : "Base Color"}</label>
                  <input className="form-control" value={sellerForm.color} onChange={(event) => updateSellerField("color", event.target.value)} />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">{isAr ? "الحالة" : "Status"}</label>
                  <select className="form-select" value={sellerForm.status} onChange={(event) => updateSellerField("status", event.target.value)}>
                    <option value="active">{isAr ? "نشط" : "Active"}</option>
                    <option value="hidden">{isAr ? "مخفي" : "Hidden"}</option>
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      id="seller-digital-product"
                      className="form-check-input"
                      type="checkbox"
                      checked={sellerForm.isDigital}
                      onChange={(event) =>
                        setSellerForm((current) => ({
                          ...current,
                          isDigital: event.target.checked,
                          stock: event.target.checked ? "" : current.stock,
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="seller-digital-product">
                      {isAr ? "منتج رقمي" : "Digital Product"}
                    </label>
                  </div>
                </div>
                {sellerForm.isDigital ? (
                  <>
                    <div className="col-12 col-md-6">
                      <label className="form-label">{isAr ? "ارفع الملف الرقمي" : "Upload Digital File"}</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (!file) {
                            setSellerForm((current) => ({
                              ...current,
                              digitalFile: null,
                              digitalFilePath: current.digitalFilePath,
                            }));
                            return;
                          }
                          const validation = validateDigitalFile(file);
                          if (!validation.valid) {
                            setDigitalFileError(validation.error);
                            event.target.value = "";
                            return;
                          }
                          setDigitalFileError("");
                          setSellerForm((current) => ({
                            ...current,
                            digitalFile: file,
                            digitalFilePath: file.name,
                          }));
                        }}
                      />
                      {digitalFileError && <div className="alert alert-danger py-2 mt-2 mb-2">{digitalFileError}</div>}
                      {sellerForm.digitalFile ? (
                        <div className="form-text">
                          {isAr ? "الملف المحدد:" : "Selected file:"} {sellerForm.digitalFile.name}
                        </div>
                      ) : sellerForm.digitalFilePath ? (
                        <div className="form-text">
                          {isAr ? "يوجد ملف رقمي محفوظ لهذا المنتج." : "A secure digital file is already saved for this product."}
                        </div>
                      ) : (
                        <div className="form-text">
                          {isAr ? "لن يكون المنتج الرقمي متاحًا للشراء بدون ملف صالح." : "Digital products cannot be sold without a valid file."}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">{isAr ? "مرجع الملف الحالي" : "Current File Reference"}</label>
                      <input
                        className="form-control"
                        value={sellerForm.digitalFilePath}
                        readOnly
                        placeholder={isAr ? "سيظهر اسم الملف أو المرجع المحفوظ هنا" : "The saved file name or reference will appear here"}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-12 col-md-4">
                    <label className="form-label">{isAr ? "مخزون إجمالي مبدئي" : "Initial Total Stock"}</label>
                    <input type="number" min="0" className="form-control" value={sellerForm.stock} onChange={(event) => updateSellerField("stock", event.target.value)} />
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-dark" type="submit" disabled={savingSellerProduct}>
                  {savingSellerProduct
                    ? isAr
                      ? "جارٍ الحفظ..."
                      : "Saving..."
                    : sellerForm.id
                      ? isAr
                        ? "تحديث وإعادة إرسال"
                        : "Update and Resubmit"
                      : isAr
                        ? "إرسال المنتج"
                        : "Submit Product"}
                </button>
                {sellerForm.id ? (
                  <button className="btn btn-outline-secondary" type="button" onClick={resetSellerForm}>
                    {isAr ? "إلغاء" : "Cancel"}
                  </button>
                ) : null}
              </div>
            </form>

            {renderVariantManager()}

            <hr className="my-4" />

            {sellerProducts.length === 0 ? (
              <div className="text-muted">
                {isAr ? "لا توجد منتجات بائع مضافة حتى الآن." : "No seller products have been submitted yet."}
              </div>
            ) : isMobile ? (
              <div className="provider-center__mobile-list">
                {sellerProducts.map((product) => (
                  <article className="provider-center__mobile-item" key={product.id}>
                    <div className="provider-center__mobile-head">
                      <div>
                        <div className="fw-semibold">{product.nameEn || product.nameAr || `#${product.id}`}</div>
                        <div className="small text-muted">
                          {isAr ? product.categoryNameAr || product.categoryNameEn : product.categoryNameEn || product.categoryNameAr}
                          {product.rejectionReason ? ` · ${product.rejectionReason}` : ""}
                        </div>
                      </div>
                      <span className={`badge rounded-pill ${getStatusBadgeClass(product.approvalStatus)}`}>
                        {getStatusLabel(product.approvalStatus, isAr)}
                      </span>
                    </div>
                    <div className="provider-center__mobile-grid">
                      <div>
                        <div className="small text-muted">{isAr ? "النوع" : "Type"}</div>
                        <div>{product.isDigital ? (isAr ? "رقمي" : "Digital") : isAr ? "مادي" : "Physical"}</div>
                      </div>
                      <div>
                        <div className="small text-muted">{isAr ? "السعر" : "Price"}</div>
                        <div>{formatMoney(product.salePrice > 0 ? product.salePrice : product.price, currency || "USD")}</div>
                      </div>
                      <div>
                        <div className="small text-muted">{isAr ? "المتغيرات" : "Variants"}</div>
                        <div>{product.variants?.length || 0}</div>
                      </div>
                    </div>
                    <div className="provider-center__mobile-actions">
                      <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => handleManageSellerVariants(product)}>
                        {isAr ? "المتغيرات" : "Variants"}
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEditSellerProduct(product)}>
                        {isAr ? "تعديل" : "Edit"}
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteSellerProduct(product)}>
                        {isAr ? "حذف" : "Delete"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{isAr ? "المنتج" : "Product"}</th>
                      <th>{isAr ? "النوع" : "Type"}</th>
                      <th>{isAr ? "الحالة" : "Approval"}</th>
                      <th>{isAr ? "السعر" : "Price"}</th>
                      <th>{isAr ? "المتغيرات" : "Variants"}</th>
                      <th>{isAr ? "الإجراءات" : "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellerProducts.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <div className="fw-semibold">{product.nameEn || product.nameAr || `#${product.id}`}</div>
                          <div className="small text-muted">
                            {isAr ? product.categoryNameAr || product.categoryNameEn : product.categoryNameEn || product.categoryNameAr}
                            {product.rejectionReason ? ` · ${product.rejectionReason}` : ""}
                          </div>
                        </td>
                        <td>{product.isDigital ? (isAr ? "رقمي" : "Digital") : isAr ? "مادي" : "Physical"}</td>
                        <td>
                          <span className={`badge rounded-pill ${getStatusBadgeClass(product.approvalStatus)}`}>
                            {getStatusLabel(product.approvalStatus, isAr)}
                          </span>
                        </td>
                        <td>{formatMoney(product.salePrice > 0 ? product.salePrice : product.price, currency || "USD")}</td>
                        <td>{product.variants?.length || 0}</td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => handleManageSellerVariants(product)}>
                              {isAr ? "المتغيرات" : "Variants"}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEditSellerProduct(product)}>
                              {isAr ? "تعديل" : "Edit"}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteSellerProduct(product)}>
                              {isAr ? "حذف" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
